import { type InputValidationState } from '@landsbankinn/ui-kit';
import { useCallback, useMemo, useReducer } from 'react';
import {
  useApplicationServerContext,
  type LoginServerState,
} from 'src/state/server';

export function usePkiSimValidation() {
  const [serverState] = useApplicationServerContext() as [LoginServerState];
  const regex = useMemo(
    () =>
      new RegExp(serverState.forms.pkiSim.fields.phoneNumber.pattern as string),
    [serverState.forms.pkiSim.fields.phoneNumber.pattern],
  );
  type State = {
    phonenumber: {
      value: string;
      validationState?: InputValidationState;
    };
    xsrf: string;
  };
  type Action =
    | {
        type: 'on change phonenumber';
        value: string;
        validationState: InputValidationState;
      }
    | {
        type: 'on xsrf token';
        value: string;
      };
  const [state, dispatch] = useReducer(
    function formReducer(formState: State, action: Action): State {
      switch (action.type) {
        case 'on change phonenumber': {
          return {
            ...formState,
            phonenumber: {
              value: action.value,
              validationState: action.validationState,
            },
          };
        }
        case 'on xsrf token': {
          return {
            ...formState,
            xsrf: action.value,
          };
        }
        default:
          return formState;
      }
    },
    {
      phonenumber: {
        value: '',
        validationState: 'unset',
      },
      xsrf: serverState.forms.pkiSim.fields.xsrf.value || '',
    },
  );

  const validate = useCallback(
    function validate(phonenumber?: string | undefined): InputValidationState {
      if (phonenumber !== undefined && phonenumber !== '') {
        if (regex.test(`${phonenumber}`)) {
          return 'valid';
        } else {
          return 'invalid';
        }
      } else {
        return 'unset';
      }
    },
    [regex],
  );
  const onPhonenumberChanged = useCallback(
    function onPhonenumberChanged(phonenumber: string) {
      const validationState = validate(phonenumber);
      // dispatch for the actual input
      dispatch({
        type: 'on change phonenumber',
        value: phonenumber,
        validationState: validationState,
      });
    },
    [validate],
  );

  const onXsrf = useCallback(function onXsrfChanged(xsrf: string) {
    dispatch({ type: 'on xsrf token', value: xsrf });
  }, []);

  return {
    onPhonenumberChanged,
    onXsrf,
    pkisimState: state,
  };
}
